import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { PhotoLibrary } from '@mui/icons-material'
import { Box, Card, IconButton, TableContainer, Tooltip } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder, TableSortValues } from '../../../../../components/GRTable/GRTable'
import { GameIcon } from '../../../../../features/game'
import { ErrorStatusCircle } from '../../../../components/ErrorStatusCircle/ErrorStatusCircle'
import { GameMarketsCell } from '../../../games/components/GameMarketsCell/GameMarketsCell'
import { GameNameCell } from '../../../games/components/GameNameCell/GameNameCell'
import {
  formatLastAnalysisDate,
  getLastAnalysisAt,
  getRoundedPowerscore,
  isLastAnalysisOlderThan,
  outdatedAnalysisMonths,
} from '../../../games/helpers/gameListHelpers'
import { getMarketRank } from '../../helpers/implementationExampleHelpers'
import { getImplementationScreenshotsPath } from '../../helpers/implementationExamplePaths'
import { ImplementationGameRow } from '../../types/ImplementationExamples'

type CustomProps = { marketIso: string }

type ImplementationGamesTableProps = {
  rows: ImplementationGameRow[]
  marketIso: string
  isLoading?: boolean
}

const noValue = '---'
const lastWhenMissing = (value?: number) => value ?? TableSortValues.AlwaysLast
const marketOf = (customProps?: CustomProps) => customProps?.marketIso ?? 'us'

export const ImplementationGamesTable: FC<ImplementationGamesTableProps> = ({ rows, marketIso, isLoading }) => {
  const { t } = useTranslation()
  const customProps = useMemo<CustomProps>(() => ({ marketIso }), [marketIso])

  const tableColumns: GRTableColumn<ImplementationGameRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:implementation_srank'),
        accessor: ({ row, customTableProps }) => getMarketRank(row.game.sranks, marketOf(customTableProps)) ?? noValue,
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => lastWhenMissing(getMarketRank(row.game.sranks, marketOf(customTableProps))),
        sortOrder: SortOrder.ASC,
        defaultSortOrder: SortOrder.ASC,
        headerCellProps: { sx: { width: 72 } },
      },
      {
        labelAccessor: t('internal-analysts:implementation_sdrank'),
        accessor: ({ row, customTableProps }) => getMarketRank(row.game.sdranks, marketOf(customTableProps)) ?? noValue,
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => lastWhenMissing(getMarketRank(row.game.sdranks, marketOf(customTableProps))),
        defaultSortOrder: SortOrder.ASC,
        headerCellProps: { sx: { width: 72 } },
      },
      {
        labelAccessor: t('internal-common:icon'),
        accessor: ({ row }) => <GameIcon src={row.game.getIcon() as string} gameName={row.game.name ?? ''} size="small" />,
        headerCellProps: { sx: { width: 62, minWidth: 62 } },
      },
      {
        labelAccessor: t('internal-common:name'),
        accessor: ({ row }) => <GameNameCell game={row.game} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.game.name ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 150 } },
      },
      {
        labelAccessor: t('internal-common:publisher'),
        accessor: ({ row }) => row.game.artist || noValue,
        sortable: true,
        sortAccessor: ({ row }) => (row.game.artist ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 110 } },
      },
      {
        labelAccessor: t('internal-common:powerscore'),
        accessor: ({ row, customTableProps }) => getRoundedPowerscore(row.game, marketOf(customTableProps)) ?? noValue,
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => lastWhenMissing(getRoundedPowerscore(row.game, marketOf(customTableProps))),
        defaultSortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: t('internal-common:last_analysis'),
        accessor: ({ row, customTableProps }) => {
          const timestamp = getLastAnalysisAt(row.game, marketOf(customTableProps))
          if (!timestamp) return noValue
          return (
            <Box component="span" whiteSpace="nowrap">
              {formatLastAnalysisDate(timestamp)}
              {isLastAnalysisOlderThan(timestamp, outdatedAnalysisMonths) && (
                <ErrorStatusCircle errorText={t('internal-common:last_analysis_over_6_months_ago')} />
              )}
            </Box>
          )
        },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => lastWhenMissing(getLastAnalysisAt(row.game, marketOf(customTableProps))),
        defaultSortOrder: SortOrder.DESC,
        headerCellProps: { sx: { minWidth: 124 } },
      },
      {
        labelAccessor: t('internal-common:stage'),
        accessor: ({ row }) => row.game.stage || noValue,
        sortable: true,
        sortAccessor: ({ row }) => (row.game.stage ?? '').toLowerCase(),
      },
      {
        labelAccessor: t('internal-common:markets'),
        accessor: ({ row }) => <GameMarketsCell game={row.game} />,
        sortable: true,
        sortAccessor: ({ row }) => row.game.markets?.length ?? 0,
      },
      {
        labelAccessor: t('internal-analysts:implementation_screenshot_count'),
        accessor: ({ row }) => row.screenshots,
        sortable: true,
        sortAccessor: ({ row }) => row.screenshots,
        defaultSortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: t('internal-analysts:implementation_comment_count'),
        accessor: ({ row }) => row.comments,
        sortable: true,
        sortAccessor: ({ row }) => row.comments,
        defaultSortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('internal-analysts:implementation_show_screenshots')}>
            <IconButton
              color="primary"
              component={RouterLink}
              to={getImplementationScreenshotsPath(row.id, marketOf(customTableProps))}
              aria-label={t('internal-analysts:implementation_show_screenshots')}
            >
              <PhotoLibrary />
            </IconButton>
          </Tooltip>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 56, minWidth: 56 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  return (
    <TableContainer component={Card}>
      <GRTable
        rows={rows}
        columns={columns}
        onColumnsUpdated={setColumns}
        rowIdKey="id"
        customProps={customProps}
        noRowsLabel={t('internal-analysts:implementation_games_none')}
        isLoading={isLoading}
        striped
        hoverable
      />
    </TableContainer>
  )
}
